import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import useTitanApp from "@/hooks/useTitanApp";
import { declinePrescriptionById } from "@/dispatchers/patientPrescriptionDispatcher";

interface DeclinePrescriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: any;
  onDecline: (prescriptionId: string, reason: string, comments: string) => void;
  isDarkMode?: boolean;
}

const declineReasons = [
  { value: "contraindication", label: "Contraindication identified" },
  { value: "drug-interaction", label: "Drug interaction concern" },
  { value: "inappropriate-dose", label: "Inappropriate dosage" },
  { value: "allergy", label: "Patient allergy" },
  { value: "clinical-unsuitability", label: "Clinical unsuitability" },
  { value: "incomplete-info", label: "Incomplete clinical information" },
  { value: "duplicate-therapy", label: "Duplicate therapy" },
  { value: "prescriber-error", label: "Prescriber error" },
  { value: "patient-request", label: "Patient request" },
  { value: "regulatory", label: "Regulatory/compliance issue" },
  { value: "other", label: "Other" },
];

export function DeclinePrescriptionModal({
  open,
  onOpenChange,
  prescription,
  onDecline,
  isDarkMode = false,
}: DeclinePrescriptionModalProps) {
  const { patientPrescriptionState } = useTitanApp();
  const { declinePrescriptionLoading: isSubmitting } = patientPrescriptionState;
  const [selectedReason, setSelectedReason] = useState("");
  const [comments, setComments] = useState("");

  const handleDecline = async () => {
    if (!selectedReason) {
      return;
    }

    const payload = {
      prescriptionId: prescription?.id || prescription?.prescriptionId || "",
      declineReason: selectedReason,
      declineComment: comments,
    };

    const result = await declinePrescriptionById(payload);

    if (result) {
      onDecline(prescription.id, selectedReason, comments);

      // Reset form
      setSelectedReason("");
      setComments("");
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedReason("");
    setComments("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-[500px] ${isDarkMode ? "bg-gray-800 text-white" : ""}`}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <DialogTitle
                className={isDarkMode ? "text-white" : "text-gray-900"}
              >
                Decline Prescription
              </DialogTitle>
              <DialogDescription
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              >
                Provide a reason for declining this prescription
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Prescription Details */}
          <div
            className={`p-3 rounded-lg border ${
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span
                  className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  Patient:
                </span>
                <span
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {prescription?.patientName}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  Medications:
                </span>
                <span
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {prescription?.medications?.length || 0} item(s)
                </span>
              </div>
            </div>
          </div>

          {/* Decline Reason */}
          <div className="space-y-2">
            <Label
              htmlFor="decline-reason"
              className={isDarkMode ? "text-gray-200" : ""}
            >
              Reason for Decline <span className="text-red-500">*</span>
            </Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger
                id="decline-reason"
                className={
                  isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
                }
              >
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {declineReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Additional Comments */}
          <div className="space-y-2">
            <Label
              htmlFor="decline-comments"
              className={isDarkMode ? "text-gray-200" : ""}
            >
              Additional Comments (Optional)
            </Label>
            <Textarea
              id="decline-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              maxLength={500}
              placeholder="Provide additional details about why this prescription is being declined..."
              rows={4}
              className={
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : ""
              }
            />
            <p
              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              These comments will be visible to the prescriber and in the audit
              trail.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className={
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : ""
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleDecline}
            disabled={!selectedReason || isSubmitting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isSubmitting ? "Declining..." : "Decline Prescription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
